import dayjs, { Dayjs } from 'dayjs';
import type { PluginFunc } from 'dayjs';
import utc from 'dayjs/plugin/utc';
import timezone from 'dayjs/plugin/timezone';
import minMax from 'dayjs/plugin/minMax';
import relativeTime from 'dayjs/plugin/relativeTime';
import customParseFormat from 'dayjs/plugin/customParseFormat';
import advancedFormat from 'dayjs/plugin/advancedFormat';
import duration from 'dayjs/plugin/duration';
import isBetween from 'dayjs/plugin/isBetween';
import isSameOrAfter from 'dayjs/plugin/isSameOrAfter';
import isSameOrBefore from 'dayjs/plugin/isSameOrBefore';
import isoWeek from 'dayjs/plugin/isoWeek';

const customIsToday: PluginFunc = (option, dayjsClass) => {
    dayjsClass.prototype.isToday = function () {
        const comparisonTemplate = 'YYYY-MM-DD';
        // @ts-expect-error - Accessing internal dayjs timezone property
        const timezone = this.$x?.$timezone;
        const now = timezone ? dayjs().tz(timezone) : dayjs();
        return this.format(comparisonTemplate) === now.format(comparisonTemplate);
    };
};

const customIsYesterday: PluginFunc = (option, dayjsClass) => {
    dayjsClass.prototype.isYesterday = function () {
        const comparisonTemplate = 'YYYY-MM-DD';
        // @ts-expect-error - Accessing internal dayjs timezone property
        const timezone = this.$x?.$timezone;
        const yesterday = timezone ? dayjs().tz(timezone).subtract(1, 'day') : dayjs().subtract(1, 'day');
        return this.format(comparisonTemplate) === yesterday.format(comparisonTemplate);
    };
};

const customIsTomorrow: PluginFunc = (option, dayjsClass) => {
    dayjsClass.prototype.isTomorrow = function () {
        const comparisonTemplate = 'YYYY-MM-DD';
        // @ts-expect-error - Accessing internal dayjs timezone property
        const timezone = this.$x?.$timezone;
        const tomorrow = timezone ? dayjs().tz(timezone).add(1, 'day') : dayjs().add(1, 'day');
        return this.format(comparisonTemplate) === tomorrow.format(comparisonTemplate);
    };
};

dayjs.extend(utc);
dayjs.extend(timezone);
dayjs.extend(minMax);
dayjs.extend(relativeTime);
dayjs.extend(customParseFormat);
dayjs.extend(advancedFormat);
dayjs.extend(duration);
dayjs.extend(isBetween);
dayjs.extend(isSameOrAfter);
dayjs.extend(isSameOrBefore);
dayjs.extend(customIsYesterday);
dayjs.extend(customIsToday);
dayjs.extend(customIsTomorrow);
dayjs.extend(isoWeek);

export default dayjs;

export function toUTCDateTimeFormat(dayjsInstance: Dayjs) {
    // we know the date is in UTC from `.utc()` thats
    // why the [Z] is "hard coded" with brackets

    return dayjsInstance.utc().format('YYYY-MM-DDTHH:mm:ss[Z]');
}

declare module 'dayjs' {
    interface Dayjs {
        isToday(): boolean;
        isYesterday(): boolean;
        isTomorrow(): boolean;
    }
}
